import React, { useState, useEffect } from 'react';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, deleteNotification, getNotificationStats, subscribeToNotifications, unsubscribeChannel, logActivity } from '../../services/supabaseService';

import Button from '../../components/ui/Button';

import NotificationFilters from './components/NotificationFilters';
import NotificationList from './components/NotificationList';
import NotificationStats from './components/NotificationStats';
import NotificationsQA from './components/NotificationsQA';

const NotificationsCenter = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, critical: 0, high: 0 });
  const [filters, setFilters] = useState({
    type: 'all',
    priority: 'all',
    isRead: 'all'
  });
  const [showQA, setShowQA] = useState(false);

  useEffect(() => {
    loadNotifications();
    loadStats();

    const channel = subscribeToNotifications(() => {
      loadNotifications();
      loadStats();
    });

    return () => {
      unsubscribeChannel(channel);
    };
  }, [filters]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams = {};
      if (filters?.type !== 'all') filterParams.type = filters?.type;
      if (filters?.priority !== 'all') filterParams.priority = filters?.priority;
      if (filters?.isRead !== 'all') filterParams.isRead = filters?.isRead === 'unread' ? false : true;

      const data = await getNotifications(filterParams);
      setNotifications(data);

      await logActivity({
        module: 'dashboard',
        action: 'export',
        entityType: 'notifications',
        entityName: 'Notifications Center',
        description: 'Viewed notifications center'
      });
    } catch (err) {
      console.error('Notifications load error:', err);
      setError(err?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await getNotificationStats();
      setStats(statsData);
    } catch (err) {
      console.error('Stats load error:', err);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      await loadNotifications();
      await loadStats();
    } catch (err) {
      console.error('Mark as read error:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      await loadNotifications();
      await loadStats();
      
      await logActivity({
        module: 'dashboard',
        action: 'bulk_action',
        entityType: 'notifications',
        entityName: 'All Notifications',
        description: 'Marked all notifications as read'
      });
    } catch (err) {
      console.error('Mark all as read error:', err);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      await loadNotifications();
      await loadStats();
    } catch (err) {
      console.error('Delete notification error:', err);
    }
  };

  if (loading && notifications?.length === 0) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-800">Error: {error}</p>
          <Button onClick={loadNotifications} className="mt-4" variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Notifications Center</h1>
          <p className="text-muted-foreground">Manage alerts, reminders, and system notifications</p>
        </div>
        <div className="flex gap-2 mt-4 md:mt-0">
          <Button
            onClick={() => setShowQA(!showQA)}
            variant="outline"
            iconName="Settings"
            iconPosition="left"
          >
            {showQA ? 'Hide QA' : 'QA / Test'}
          </Button>
          <Button
            onClick={handleMarkAllAsRead}
            variant="outline"
            iconName="CheckCheck"
            iconPosition="left"
            disabled={stats?.unread === 0}
          >
            Mark All Read
          </Button>
          <Button onClick={loadNotifications} variant="outline" iconName="RefreshCw" iconPosition="left">
            Refresh
          </Button>
        </div>
      </div>

      {/* QA Section */}
      {showQA && <NotificationsQA />}

      {/* Stats */}
      <NotificationStats stats={stats} />

      {/* Filters */}
      <NotificationFilters filters={filters} onFilterChange={setFilters} />

      {/* Notifications List */}
      <NotificationList
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default NotificationsCenter;